/* ==========================================================================
   accordion.js — DESIGN-SPEC-v2 §5. Generic [data-accordion] behaviour on
   top of native <details>/<summary>, so every consumer (home-faq now, PDP
   later) works fully with JS off before this script ever runs:
   - each <details> ships with (or without) the native `open` attribute for
     its no-JS state — this script never changes which item starts open
   - a direct-child element with [data-accordion-panel] wraps the answer
     body; its height is animated 0 <-> auto (progressive enhancement only
     — with JS off the panel is just plain content following <summary>)
   - by default only one <details> per [data-accordion] root is open at a
     time (opening one closes any other, matching both the FAQ and PDP
     accordions in the DESIGN mockup's logic class, which each track a
     single active index). Opt out with `data-accordion-multi="true"` on
     the root to let items open independently.
   - arrow-key nav: Up/Down moves focus between an accordion's <summary>
     elements, Home/End jump to the first/last (WAI-ARIA accordion pattern)
   - prefers-reduced-motion skips the height transition (instant toggle)
   Vanilla, no libraries. Safe to include once per page.
   ========================================================================== */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

  function prefersReducedMotion() {
    return !!(reducedMotion && reducedMotion.matches);
  }

  function initItem(details, group) {
    if (details.__ecolineAccordionItem) return;
    details.__ecolineAccordionItem = true;

    var summary = details.querySelector('summary');
    var panel = details.querySelector('[data-accordion-panel]');
    summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
    if (!panel) {
      // No animated panel — leave native <details> behaviour untouched
      // beyond the exclusive-open bookkeeping below.
      details.addEventListener('toggle', function () {
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
        if (details.open && !group.multi) closeSiblings(group, details);
      });
      return;
    }

    var pending = null; // 'open' | 'close' | null, while a transition runs
    var fallbackTimer = null; // finalizes even if no height transition fires

    function clearInlineHeight() {
      panel.style.height = '';
      panel.style.overflow = '';
    }

    function clearFallback() {
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
    }

    /* Shared finalizer for both the transitionend path and the timeout
       fallback (a consumer stylesheet may ship no `transition: height`,
       in which case transitionend never fires — the state machine must
       still complete instead of stranding `pending`/aria-expanded). */
    function finalize() {
      clearFallback();
      panel.removeEventListener('transitionend', onTransitionEnd);
      details.classList.remove('is-accordion-animating');
      if (pending === 'close') {
        details.open = false;
        clearInlineHeight();
      } else if (pending === 'open') {
        panel.style.height = 'auto';
        panel.style.overflow = '';
      }
      pending = null;
      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
    }

    function armFallback() {
      clearFallback();
      /* .28s is the longest height transition in the theme; 450ms leaves
         headroom without a visible lag when the transition is missing. */
      fallbackTimer = setTimeout(finalize, 450);
    }

    function onTransitionEnd(e) {
      if (e.target !== panel || e.propertyName !== 'height') return;
      finalize();
    }

    function openPanel() {
      if (details.open && pending !== 'close') return;
      pending = 'open';
      details.open = true;
      details.classList.add('is-accordion-animating');
      panel.style.overflow = 'hidden';
      var start = panel.style.height && panel.style.height !== 'auto'
        ? parseFloat(panel.style.height) || 0
        : 0;
      panel.style.height = start + 'px';
      // Force reflow so the browser registers the start height before the
      // next frame sets the target — otherwise both writes coalesce and
      // there is nothing to transition from.
      void panel.offsetHeight;
      var target = panel.scrollHeight;
      if (prefersReducedMotion()) {
        clearFallback();
        panel.style.height = 'auto';
        panel.style.overflow = '';
        details.classList.remove('is-accordion-animating');
        pending = null;
        summary.setAttribute('aria-expanded', 'true');
        return;
      }
      panel.addEventListener('transitionend', onTransitionEnd);
      armFallback();
      window.requestAnimationFrame(function () {
        panel.style.height = target + 'px';
      });
    }

    function closePanel() {
      if (!details.open) return;
      pending = 'close';
      details.classList.add('is-accordion-animating');
      panel.style.overflow = 'hidden';
      panel.style.height = panel.scrollHeight + 'px';
      void panel.offsetHeight;
      if (prefersReducedMotion()) {
        clearFallback();
        details.open = false;
        clearInlineHeight();
        details.classList.remove('is-accordion-animating');
        pending = null;
        summary.setAttribute('aria-expanded', 'false');
        return;
      }
      panel.addEventListener('transitionend', onTransitionEnd);
      armFallback();
      window.requestAnimationFrame(function () {
        panel.style.height = '0px';
      });
    }

    summary.addEventListener('click', function (e) {
      e.preventDefault();
      /* aria-expanded reflects the INTENT, not details.open — closePanel()
         deliberately leaves details.open=true until the transition ends, so
         reading details.open here would announce a collapsing item as
         "expanded". finalize() re-syncs it from the settled state. */
      if (details.open && pending !== 'close') {
        closePanel();
        summary.setAttribute('aria-expanded', 'false');
      } else {
        if (!group.multi) closeSiblings(group, details);
        openPanel();
        summary.setAttribute('aria-expanded', 'true');
      }
    });

    details.__ecolineAccordionClose = closePanel;
  }

  function closeSiblings(group, current) {
    group.items.forEach(function (details) {
      if (details === current || !details.open) return;
      if (details.__ecolineAccordionClose) {
        details.__ecolineAccordionClose();
      } else {
        details.open = false;
      }
      var s = details.querySelector('summary');
      if (s) s.setAttribute('aria-expanded', 'false');
    });
  }

  function initArrowNav(root) {
    root.addEventListener('keydown', function (e) {
      var key = e.key;
      if (key !== 'ArrowDown' && key !== 'ArrowUp' && key !== 'Home' && key !== 'End') return;
      var target = e.target;
      if (!target || target.tagName !== 'SUMMARY') return;
      var summaries = Array.prototype.slice.call(root.querySelectorAll('summary'));
      var i = summaries.indexOf(target);
      if (i === -1) return;
      e.preventDefault();
      var nextIndex = i;
      if (key === 'ArrowDown') nextIndex = (i + 1) % summaries.length;
      else if (key === 'ArrowUp') nextIndex = (i - 1 + summaries.length) % summaries.length;
      else if (key === 'Home') nextIndex = 0;
      else if (key === 'End') nextIndex = summaries.length - 1;
      summaries[nextIndex].focus();
    });
  }

  function initAccordion(root) {
    if (root.__ecolineAccordion) return;
    root.__ecolineAccordion = true;

    var items = Array.prototype.slice.call(root.querySelectorAll('details'));
    if (!items.length) return;

    var group = {
      items: items,
      multi: root.getAttribute('data-accordion-multi') === 'true'
    };

    items.forEach(function (details) { initItem(details, group); });
    initArrowNav(root);
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('[data-accordion]');
    Array.prototype.forEach.call(nodes, initAccordion);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme editor: initialise re-rendered sections.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
